import { useContext } from 'react';
import { AnalyticsContext } from '@src/contexts/analytics-context';
import {
  TrackClickFunc,
  TrackEventFunc,
  TrackImpressionFunc,
  TrackPageViewFunc,
} from '@src/@types/analytics';

/**
 * Consume within components to obtain functions to track analytics events
 * like page views, impressions, and clicks.
 *
 * All `track*` functions provided by this hook are stable and can safely
 * be used in useEffect/useCallback dependencies without triggering re-runs.
 */
export const useAnalytics: () => {
  /**
   * Can be used to block rendering for components which need SPA Analytics
   * initialized before they render. This should be very rare (e.g. external
   * libraries), because the functions exposed by this hook can be invoked
   * at any time, regardless of where the SPA Analytics library is in its
   * initialization lifecycle.
   */
  initialized: boolean;
  trackPageView: TrackPageViewFunc;
  trackClick: TrackClickFunc;
  trackEvent: TrackEventFunc;
  trackImpression: TrackImpressionFunc;
} = () => {
  const {
    analyticsInitialized,
    trackPageView,
    trackClick,
    trackEvent,
    trackImpression,
  } = useContext(AnalyticsContext);

  return {
    initialized: analyticsInitialized,
    trackPageView,
    trackClick,
    trackEvent,
    trackImpression,
  };
};
